import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import {
  EhDataTableShell,
  HeroHeaderLogo,
  SortableHeader,
  TableSortResetBar,
  sortRowsByKey,
  useTableSort,
} from "@/components/dataTable";
import {
  userIsAbrechnung,
  userIsAnrechnung,
  userIsBuero,
  userIsManagement,
  type CurrentUser,
} from "@/lib/auth";
import { isElectronWithOpenFolder } from "@/lib/electron";
import {
  lieferscheinBillingStatusLabel,
  markLieferscheinAtContact,
  type LieferscheinSummary,
} from "@/lib/lieferscheine";
import { getAuftragSharepointWebUrl } from "@/lib/projects";
import { toast } from "@/lib/toast";
import {
  clampTimesheetColumnWidth,
  getTimesheetActionColWidth,
} from "@/pages/stundennachweise/columns";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import {
  formatInvoiceValue,
  formatLieferscheinPeriod,
  formatLieferscheinProjectLabel,
  getLieferscheinContactAgeInfo,
  getLieferscheinContactName,
  lieferscheinCanMarkAtContact,
  lieferscheinStatusBadgeClass,
} from "@/pages/lieferscheine/lieferscheinListLogic";

const HERO_PROJECT_BASE = "https://login.hero-software.de/partner/Projects/view/";
const HERO_LOGO_LIGHT = "/static/img/hero-logo.png";
const HERO_LOGO_DARK = "/static/img/hero-logo-dark.png";
const HERO_LEGACY_LOGO_LIGHT = "/static/img/plancraft-logo.png";
const HERO_LEGACY_LOGO_DARK = "/static/img/plancraft-logo-dark.png";

const DATA_COLUMNS = [
  "number",
  "project",
  "contact",
  "period",
  "status",
  "at_contact",
  "invoice_number",
  "invoice_value",
] as const;

const COLUMN_LABELS: Record<string, string> = {
  number: "Nr.",
  project: "Projekt",
  contact: "Ansprechpartner",
  period: "Zeitraum",
  status: "LS-Status",
  at_contact: "LS bei AP",
  invoice_number: "Rechnungsnr.",
  invoice_value: "Rechnungsbetrag",
  actions: "Aktion",
};

const COLUMN_WIDTHS: Record<string, number> = {
  number: 128,
  project: 200,
  contact: 140,
  period: 130,
  status: 132,
  at_contact: 168,
  invoice_number: 120,
  invoice_value: 112,
};

function lieferscheinTableColumns(user: CurrentUser | null): string[] {
  const columns: string[] = [...DATA_COLUMNS];
  if (userIsBuero(user)) columns.push("actions");
  return columns;
}

export function LieferscheinTable({
  rows,
  user,
  loading,
  widths = {},
  onRowOpen,
  onContactToggled,
  onColumnWidthChange,
}: {
  rows: LieferscheinSummary[];
  user: CurrentUser | null;
  loading: boolean;
  widths?: Record<string, number>;
  onRowOpen: (row: LieferscheinSummary) => void;
  onContactToggled?: (dokumentId: number, atContact: boolean) => void;
  onColumnWidthChange?: (key: string, width: number) => void;
}) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { sort, toggle, reset } = useTableSort();
  const columns = useMemo(() => lieferscheinTableColumns(user), [user]);
  const sortedRows = useMemo(
    () => sortRowsByKey(rows, sort, (row, key) => sortValue(row, key)),
    [rows, sort],
  );

  if (!isDesktop) {
    return (
      <MobileCards rows={sortedRows} user={user} loading={loading} onRowOpen={onRowOpen} />
    );
  }

  return (
    <DesktopTable
      rows={sortedRows}
      columns={columns}
      widths={widths}
      user={user}
      loading={loading}
      onRowOpen={onRowOpen}
      onContactToggled={onContactToggled}
      onColumnWidthChange={onColumnWidthChange}
      sort={sort}
      onSort={toggle}
      onSortReset={reset}
    />
  );
}

function DesktopTable({
  rows,
  columns,
  widths,
  user,
  loading,
  onRowOpen,
  onContactToggled,
  onColumnWidthChange,
  sort,
  onSort,
  onSortReset,
}: {
  rows: LieferscheinSummary[];
  columns: string[];
  widths: Record<string, number>;
  user: CurrentUser | null;
  loading: boolean;
  onRowOpen: (row: LieferscheinSummary) => void;
  onContactToggled?: (dokumentId: number, atContact: boolean) => void;
  onColumnWidthChange?: (key: string, width: number) => void;
  sort: { key: string; direction: "asc" | "desc" } | null;
  onSort: (key: string) => void;
  onSortReset: () => void;
}) {
  const showElectronFolder = userIsBuero(user) && isElectronWithOpenFolder();
  const dataColumns = columns.filter((col) => col !== "actions");
  const actionColumns = columns.filter((col) => col === "actions");
  const actionWidth = getTimesheetActionColWidth(showElectronFolder);

  const dataWidths = dataColumns.map((col) => {
    const stored = Number(widths[col]);
    return Number.isFinite(stored) && stored > 0
      ? clampTimesheetColumnWidth(stored)
      : (COLUMN_WIDTHS[col] ?? 120);
  });

  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: dataColumns.length > 0,
    syncKey: [dataColumns.join("|"), dataWidths.join(","), rows.length].join("§"),
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);
    const startX = event.clientX;
    const startWidth = dataWidths[idx] ?? COLUMN_WIDTHS[colKey] ?? 120;
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampTimesheetColumnWidth(width);
      if (colEl) colEl.style.width = `${lastWidth}px`;
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) floatCol.style.width = `${lastWidth}px`;
    }

    function onMove(e: PointerEvent) {
      applyWidth(startWidth + (e.clientX - startX));
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) onColumnWidthChange?.(colKey, lastWidth);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  const thead = (omitResizers: boolean) => (
    <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {dataColumns.map((col, idx) => (
          <th
            key={col}
            scope="col"
            className="eh-data-th whitespace-normal break-words px-3 py-3 text-left font-semibold leading-snug"
          >
            <SortableHeader
              label={COLUMN_LABELS[col] ?? col}
              columnKey={col}
              sort={sort}
              onSort={onSort}
            />
            {!omitResizers ? (
              <span
                className={clsx("eh-data-resizer", activeResize === col && "is-active")}
                role="separator"
                aria-orientation="vertical"
                title="Spaltenbreite ziehen"
                onPointerDown={(e) => startResize(e, col, idx)}
              />
            ) : null}
          </th>
        ))}
        {actionColumns.map((col) => (
          <th
            key={col}
            scope="col"
            className="eh-data-action-cell whitespace-nowrap py-3 text-center text-[10px] font-semibold uppercase tracking-wide"
            style={{ width: `${actionWidth}px`, right: 0 }}
          >
            <span className="sr-only">{COLUMN_LABELS.actions}</span>
            <ActionHeader showElectronFolder={showElectronFolder} />
          </th>
        ))}
      </tr>
    </thead>
  );

  return (
    <>
      <TableSortResetBar visible={sort != null} onReset={onSortReset} />
      <EhDataTableShell ref={tableWrapRef}>
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          <colgroup>
            {dataColumns.map((col, idx) => (
              <col
                key={col}
                ref={(el) => {
                  colRefs.current[col] = el;
                }}
                style={{ width: `${dataWidths[idx]}px` }}
              />
            ))}
            {actionColumns.map((col) => (
              <col key={col} style={{ width: `${actionWidth}px` }} />
            ))}
          </colgroup>
          {thead(false)}
          <tbody className="divide-y divide-separator bg-card">
            {rows.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-label-tertiary"
                >
                  Keine Lieferscheine gefunden.
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const archived = row.billing_status === "archived" || Boolean(row.billing_archived_at);
              return (
                <tr
                  key={row.id}
                  onClick={() => onRowOpen(row)}
                  className={clsx(
                    "eh-data-row cursor-pointer align-middle transition",
                    row.billing_questions_open && "bg-status-red/5",
                    archived && "opacity-70",
                  )}
                  title={archived ? "Archiviert" : undefined}
                >
                  {dataColumns.map((col) => (
                    <td
                      key={col}
                      className="eh-data-body-cell min-w-0 align-middle whitespace-normal break-words px-3 py-3 text-label"
                      onClick={col === "at_contact" ? (e) => e.stopPropagation() : undefined}
                    >
                      <Cell col={col} row={row} user={user} onContactToggled={onContactToggled} />
                    </td>
                  ))}
                  {actionColumns.map((col) => (
                    <td
                      key={col}
                      className="eh-data-action-cell align-middle p-0 text-center leading-none"
                      style={{ width: `${actionWidth}px`, right: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ActionCell row={row} showElectronFolder={showElectronFolder} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </EhDataTableShell>

      {floating && typeof document !== "undefined"
        ? createPortal(
            <div
              className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
              style={{ top: floating.top, left: floating.left, width: floating.width }}
              aria-hidden
            >
              <div
                className="pointer-events-auto eh-floating-table-header-sync"
                style={{
                  width: floating.tableWidth,
                  transform: `translateX(${-floating.scrollLeft}px)`,
                  ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
                }}
              >
                <table className="eh-data-table w-full text-sm">
                  <colgroup>
                    {dataColumns.map((col, idx) => (
                      <col
                        key={col}
                        ref={(el) => {
                          floatingColRefs.current[col] = el;
                        }}
                        style={{ width: `${dataWidths[idx]}px` }}
                      />
                    ))}
                    {actionColumns.map((col) => (
                      <col key={col} style={{ width: `${actionWidth}px` }} />
                    ))}
                  </colgroup>
                  {thead(false)}
                </table>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function ActionHeader({ showElectronFolder }: { showElectronFolder: boolean }) {
  return (
    <span className="inline-flex items-center justify-center gap-0" aria-hidden>
      <span className="inline-flex w-11 justify-center">
        <img
          src="/static/icons/apple-touch-icon-180.png"
          alt=""
          width={18}
          height={18}
          className="object-contain opacity-70"
          decoding="async"
        />
      </span>
      {showElectronFolder ? (
        <span className="inline-flex w-11 justify-center text-[1.25rem] leading-none opacity-70">
          📂
        </span>
      ) : null}
      <span className="inline-flex w-11 justify-center">
        <HeroHeaderLogo className="h-[22px] w-[22px] object-contain opacity-70" />
      </span>
    </span>
  );
}

function Cell({
  col,
  row,
  user,
  onContactToggled,
}: {
  col: string;
  row: LieferscheinSummary;
  user: CurrentUser | null;
  onContactToggled?: (dokumentId: number, atContact: boolean) => void;
}) {
  switch (col) {
    case "number":
      return <NumberCell row={row} user={user} />;
    case "project":
      return <span className="break-words">{formatLieferscheinProjectLabel(row) || "—"}</span>;
    case "contact": {
      const name = getLieferscheinContactName(row);
      return <span>{name === "Unbekannt" ? "—" : name}</span>;
    }
    case "period":
      return <span>{formatLieferscheinPeriod(row) || "—"}</span>;
    case "status":
      return (
        <TimesheetBadge className={lieferscheinStatusBadgeClass(row, user)}>
          {lieferscheinBillingStatusLabel(row.billing_status || row.status)}
        </TimesheetBadge>
      );
    case "at_contact":
      return <AtContactCell row={row} user={user} onToggled={onContactToggled} />;
    case "invoice_number":
      return <span>{row.rechnung_nummer || "—"}</span>;
    case "invoice_value":
      return <span className="tabular-nums">{formatInvoiceValue(row.invoice_value)}</span>;
    default:
      return <span>—</span>;
  }
}

function NumberCell({ row, user }: { row: LieferscheinSummary; user: CurrentUser | null }) {
  const isAbrechnung = userIsAbrechnung(user);
  const isAnrechnung = userIsAnrechnung(user);
  const isManagement = userIsManagement(user);
  const hasQuestions =
    Boolean(row.has_billing_questions) || Number(row.billing_question_count ?? 0) > 0;
  const questionsOpen =
    Boolean(row.billing_questions_open) || Number(row.billing_question_open_count ?? 0) > 0;
  const showRueckfrage = (isAbrechnung || isAnrechnung || isManagement) && hasQuestions;
  const billingClass = !hasQuestions
    ? null
    : questionsOpen
      ? "bg-status-red text-white"
      : "bg-status-green text-white";
  const neu = isAbrechnung && row.is_new_for_billing === true;

  return (
    <span className="flex flex-wrap items-center gap-1">
      <TimesheetBadge className={lieferscheinStatusBadgeClass(row, user)}>
        {row.display_number || "—"}
      </TimesheetBadge>
      {neu ? <TimesheetBadge warning>Neu</TimesheetBadge> : null}
      {showRueckfrage && billingClass ? (
        <TimesheetBadge className={billingClass}>Rückfrage</TimesheetBadge>
      ) : null}
    </span>
  );
}

function AtContactCell({
  row,
  user,
  onToggled,
}: {
  row: LieferscheinSummary;
  user: CurrentUser | null;
  onToggled?: (dokumentId: number, atContact: boolean) => void;
}) {
  const contactAge = getLieferscheinContactAgeInfo(row);
  const billingLogin = String(row.billing_assignee_login || "").trim();
  const billingBadge =
    userIsBuero(user) && billingLogin ? (
      <TimesheetBadge compact className="bg-fill-tertiary text-label-secondary">
        {billingLogin}
      </TimesheetBadge>
    ) : null;

  if (!lieferscheinCanMarkAtContact(row)) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {billingBadge ?? <span className="text-sm text-label-tertiary">—</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="checkbox"
        className="h-4 w-4 shrink-0 rounded border-separator"
        checked={Boolean(row.at_contact)}
        aria-label="Beim Ansprechpartner"
        onChange={async (e) => {
          const next = e.target.checked;
          const previous = Boolean(row.at_contact);
          onToggled?.(row.id, next);
          try {
            await markLieferscheinAtContact(row.id, next);
          } catch (err) {
            onToggled?.(row.id, previous);
            toast.error(err instanceof Error ? err.message : "Markierung fehlgeschlagen.");
          }
        }}
      />
      {contactAge ? (
        <TimesheetBadge compact className={contactAge.threshold.className}>
          {contactAge.threshold.label}
        </TimesheetBadge>
      ) : null}
      {billingBadge}
    </div>
  );
}

function ActionCell({
  row,
  showElectronFolder,
}: {
  row: LieferscheinSummary;
  showElectronFolder: boolean;
}) {
  const isDark = useIsDarkTheme();
  const hasFolder = Boolean(row.ordnerpfad || row.sharepoint_folder_id);
  const pid = row.hero_project_id != null ? Number(row.hero_project_id) : null;
  const legacy = row.legacy_import === true;
  const heroSrc = legacy
    ? isDark
      ? HERO_LEGACY_LOGO_DARK
      : HERO_LEGACY_LOGO_LIGHT
    : isDark
      ? HERO_LOGO_DARK
      : HERO_LOGO_LIGHT;

  return (
    <div className="inline-flex items-center justify-center">
      <ProjectOverviewButton
        auftragId={row.auftrag_id}
        className="eh-data-action-btn shrink-0 rounded-lg border-0 bg-transparent shadow-none"
      />
      {showElectronFolder ? (
        hasFolder ? (
          <button
            type="button"
            className="eh-data-action-btn shrink-0"
            title="Ordner öffnen"
            aria-label="Ordner öffnen"
            onClick={(e) => void openFolder(e, row)}
          >
            <span className="text-[1.25rem] leading-none" aria-hidden>
              📂
            </span>
          </button>
        ) : (
          <span
            title="Kein Projektordner hinterlegt"
            className="eh-data-action-btn inline-flex shrink-0 cursor-default items-center justify-center"
          >
            <span className="text-[1.25rem] leading-none opacity-35 grayscale" aria-hidden>
              📂
            </span>
          </span>
        )
      ) : null}
      {pid && pid > 0 ? (
        <a
          href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(pid))}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Hero-Projekt öffnen"
          className="eh-data-action-btn shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <img src={heroSrc} alt="Hero" style={{ width: 22, height: 22, objectFit: "contain" }} />
        </a>
      ) : (
        <span
          title="Kein Hero-Projekt hinterlegt"
          className="eh-data-action-btn inline-flex shrink-0 cursor-default items-center justify-center"
          style={{ opacity: 0.5 }}
        >
          <img
            src={isDark ? HERO_LEGACY_LOGO_DARK : HERO_LEGACY_LOGO_LIGHT}
            alt="Hero"
            style={{ width: 22, height: 22, objectFit: "contain" }}
          />
        </span>
      )}
    </div>
  );
}

function MobileCards({
  rows,
  user,
  loading,
  onRowOpen,
}: {
  rows: LieferscheinSummary[];
  user: CurrentUser | null;
  loading: boolean;
  onRowOpen: (row: LieferscheinSummary) => void;
}) {
  if (!rows.length && !loading) {
    return <p className="py-8 text-center text-sm text-label-tertiary">Keine Lieferscheine gefunden.</p>;
  }
  return (
    <ul className="divide-y divide-separator rounded-ios-lg bg-card ring-1 ring-separator/35">
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            className="flex w-full flex-col gap-1 px-4 py-3 text-left transition hover:bg-fill-tertiary/50"
            onClick={() => onRowOpen(row)}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-label">{row.display_number || "—"}</span>
              <TimesheetBadge className={lieferscheinStatusBadgeClass(row, user)}>
                {lieferscheinBillingStatusLabel(row.billing_status || row.status)}
              </TimesheetBadge>
            </div>
            <span className="text-sm text-label">{formatLieferscheinProjectLabel(row) || "—"}</span>
            <span className="text-xs text-label-secondary">
              {[getLieferscheinContactName(row), formatLieferscheinPeriod(row)]
                .filter((part) => part && part !== "Unbekannt")
                .join(" · ") || "—"}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function sortValue(row: LieferscheinSummary, col: string): string | number | boolean | null {
  switch (col) {
    case "number":
      return row.display_number || "";
    case "project":
      return formatLieferscheinProjectLabel(row);
    case "contact":
      return getLieferscheinContactName(row);
    case "period":
      return row.belegdatum || row.lieferdatum || row.customer_signed_at || "";
    case "status":
      return lieferscheinBillingStatusLabel(row.billing_status || row.status);
    case "at_contact":
      return Boolean(row.at_contact);
    case "invoice_number":
      return row.rechnung_nummer || "";
    case "invoice_value":
      return row.invoice_value == null ? null : Number(row.invoice_value);
    default:
      return "";
  }
}

async function openFolder(e: React.MouseEvent, row: LieferscheinSummary) {
  e.stopPropagation();
  const path = String(row.ordnerpfad || "").trim();
  if (!path) {
    if (row.auftrag_id) {
      const webUrl = await getAuftragSharepointWebUrl(Number(row.auftrag_id));
      if (webUrl) window.open(webUrl, "_blank", "noopener");
    }
    return;
  }
  const opener = window.envAPI?.openFolder;
  if (typeof opener !== "function") return;
  const err = await opener(path);
  if (err) {
    if (row.auftrag_id) {
      const webUrl = await getAuftragSharepointWebUrl(Number(row.auftrag_id));
      if (webUrl) {
        window.open(webUrl, "_blank", "noopener");
        return;
      }
    }
    toast.error(String(err));
  }
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

function useIsDarkTheme(): boolean {
  const [isDark, setIsDark] = useState(() => readIsDark());
  useEffect(() => {
    const root = document.documentElement;
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() => setIsDark(readIsDark()));
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme", "class"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function readIsDark(): boolean {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === "dark") return true;
  if (root.classList.contains("dark")) return true;
  return false;
}
